import { SapResponse } from "../types/sap";

import { ReactNode, useState } from "react";
import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useCsConfigurationApi } from "@plansoft-configuration-renderer/framework";

import CustomDialog from "./CustomDialog/CustomDialog";
import CustomDialogActions from "./CustomDialog/CustomDialogActions";

interface SapDialogProps {
  disabled?: boolean;
  renderTrigger: (props: {
    onClick: (event: MouseEvent<unknown>) => void;
  }) => ReactNode;
  handleSaveClick: (type: "SAP") => Promise<string>;
}

export default function SapDialog({
  disabled,
  renderTrigger,
  handleSaveClick,
}: SapDialogProps): ReactNode {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [sapResponse, setSapResponse] = useState<SapResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { reloadConfiguration } = useCsConfigurationApi();

  const handleOpen = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setSapResponse(null);
    try {
      const textResponse = await handleSaveClick("SAP");
      const parsedResponse: SapResponse = JSON.parse(textResponse);

      const requestMessage = parsedResponse?._requestMessage;
      if (requestMessage) {
        Object.keys(requestMessage).forEach((key) => {
          const value = requestMessage[key];
          if (typeof value === "string") {
            try {
              requestMessage[key] = JSON.parse(value);
            } catch {}
          }
        });
      }

      setSapResponse(parsedResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (): void => {
    reloadConfiguration();
    setSapResponse(null);
    setError(null);
    setIsLoading(false);
  };

  return (
    <CustomDialog
      disabled={disabled}
      renderTrigger={({ onClick }) =>
        renderTrigger({
          onClick: (event) => {
            onClick(event);
            handleOpen();
          },
        })
      }
      dialog={{
        onClose: handleClose,
        dialogProps: {
          maxWidth: "md",
        },
        title: t("sap_title"),
        actions: ({ closeDialog }) => (
          <CustomDialogActions
            variant="step1"
            textCancel={t("close_configuration_button")}
            onCancel={() => {
              closeDialog();
            }}
          />
        ),
        content: (
          <Box minHeight={120} minWidth={400}>
            {isLoading && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={60}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {sapResponse && (
              <Box>
                {Object.values(sapResponse._requestMessage ?? {}).map(
                  (requestMessage, i) => (
                    <Box key={i}>
                      <Typography sx={{ mb: 2 }}>
                        {requestMessage.ErrorNotice || t("sap.create_success")}
                      </Typography>

                      <TableContainer component={Box}>
                        <Table aria-label="sap-table">
                          <TableBody>
                            {requestMessage.ItemList?.map((item, index) => (
                              <TableRow
                                key={index}
                                sx={{
                                  "& .MuiTableCell-root": {
                                    padding: "0.25rem 0",
                                  },
                                }}
                              >
                                <TableCell
                                  sx={{
                                    width: "3rem",
                                  }}
                                >
                                  {item.ErrorNotice ? (
                                    <ErrorIcon color="error" />
                                  ) : (
                                    <CheckCircleIcon color="primary" />
                                  )}
                                </TableCell>
                                {item.ErrorNotice ? (
                                  <TableCell colSpan={3}>
                                    {item.ErrorNotice}
                                  </TableCell>
                                ) : (
                                  <>
                                    <TableCell>{item.Description}</TableCell>
                                    <TableCell>
                                      {item.ConfigurationID}
                                    </TableCell>
                                    <TableCell>{item.MaterialNumber}</TableCell>
                                  </>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ),
                )}
              </Box>
            )}
          </Box>
        ),
      }}
    />
  );
}
